import { Component, type ErrorInfo, type ReactNode } from "react";
import { reportError } from "@/utils/logger";

interface ErrorBoundaryProps {
  readonly children: ReactNode;
  readonly fallback?: ReactNode;
}

interface ErrorBoundaryState {
  readonly hasError: boolean;
  readonly error: Error | null;
}

/**
 * Catches unhandled React render errors and reports them to the backend.
 * Wrap this around <App /> in main.tsx.
 */
export default class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    reportError(error, {
      componentStack: info.componentStack ?? undefined,
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
          <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-8 text-center shadow-sm">
            <div className="mb-4 text-4xl">
              <svg
                className="mx-auto h-12 w-12 text-red-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                />
              </svg>
            </div>
            <h2 className="mb-2 text-lg">
              Er ging iets mis
            </h2>
            <p className="mb-6 text-sm text-gray-500">
              Er is een onverwachte fout opgetreden. Het probleem is automatisch
              gemeld aan ons team.
            </p>
            <div className="flex justify-center gap-3">
              <button
                onClick={this.handleReset}
                className="dh-btn dh-btn--action"
              >
                Opnieuw proberen
              </button>
              <button
                onClick={() => {
                  window.location.href = "/";
                }}
                className="dh-btn dh-btn--secondary"
              >
                Naar startpagina
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
